// ============================================
// TEEMPAD EVENTS - Firebase Config
// ============================================
//
// Setup: Create .env with:
//   VITE_FIREBASE_API_KEY=...
//   VITE_FIREBASE_AUTH_DOMAIN=...
//   VITE_FIREBASE_PROJECT_ID=...
//   VITE_FIREBASE_STORAGE_BUCKET=...
//   VITE_FIREBASE_MESSAGING_SENDER_ID=...
//   VITE_FIREBASE_APP_ID=...
//
// Optional: VITE_USE_FIREBASE_EMULATORS=true
// ============================================

import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore'
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth'
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions'
import { getStorage as firebaseGetStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage'
import { devLog, errorLog } from '@/utils/logger'

// Nota: Para setDoc, updateDoc, addDoc usar @/services/firestore (sanitiza undefined)

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
}

const isFirebaseConfigured = (): boolean => {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.projectId &&
    firebaseConfig.apiKey !== 'undefined'
  )
}

let app: FirebaseApp | null = null
let db: Firestore | null = null
let auth: Auth | null = null
let functions: Functions | null = null
let storage: FirebaseStorage | null = null

if (isFirebaseConfigured()) {
  try {
    app = initializeApp(firebaseConfig)
    db = getFirestore(app)
    auth = getAuth(app)
    functions = getFunctions(app, 'europe-west1')
    storage = firebaseGetStorage(app)

    const useEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true'
    if (useEmulators && import.meta.env.DEV) {
      const emulatorHost = import.meta.env.VITE_FIREBASE_EMULATOR_HOST || 'localhost'
      connectFirestoreEmulator(db, emulatorHost, 8080)
      connectAuthEmulator(auth, `http://${emulatorHost}:9099`, { disableWarnings: true })
      connectFunctionsEmulator(functions, emulatorHost, 5001)
      connectStorageEmulator(storage, emulatorHost, 9199)
      devLog.log('🔥 Firebase Emulators connected')
    }
    devLog.log('✅ Firebase initialized')
  } catch (error) {
    errorLog.error('❌ Firebase init error:', error)
  }
} else {
  devLog.warn('⚠️ Firebase not configured. Running in offline mode.')
}

export { app, db, auth, functions, storage }
export { isFirebaseConfigured }
export default app
